'use client'

import { datadogRum } from '@datadog/browser-rum'
import { COOKIE_KEYS } from 'constants/common'
import { useIsOsanoCmpActive } from 'context/common/OsanoCmpContext'
import UserContext from 'context/common/User/UserContext'
import { getUserInfoById } from 'dataSource/users/routes'
import useApi from 'hooks/useApi'
import useThriveAuth from 'hooks/useThriveAuth'
import { CustomAccessToken } from 'hooks/useThriveAuth/types'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'
import { subscribeToOsanoAnalyticsConsent } from 'lib/osanoAnalyticsConsent'
import { useEffect, useState } from 'react'
import { hasError } from 'utils/common'
import { logIdentity } from 'utils/logIdentity'
import {
  initializeSplitAnonymousClient,
  initializeSplitUserClient,
  setSplitAnalyticsConsent,
} from 'utils/treatment'
import { IdentityUserTypes } from 'dataSource/auth/types'
import { setB2BDetails } from 'state/booking/actions'
import {
  addKeywordToStoredSearchQuery,
  convertToB2bDetails,
} from 'utils/b2bDetails'
import _pick from 'lodash/pick'
import { Dispatch } from 'redux'

const UserContextWrapper: React.FC<{ dispatch?: Dispatch }> = ({
  children,
  dispatch,
}) => {
  const initialUserValue = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    address: null,
    businessPartnerId: null,
    isPartnerAssociationActive: false,
    businessPartner: null,
  }
  const {
    isAuthenticated,
    isLoading,
    isSupportUser,
    isThirdPartyAgent,
    isClinician,
    getAccessTokenSilently,
  } = useThriveAuth()
  const isOsanoCmpActive = useIsOsanoCmpActive()
  const { request: userInfoRequest } = useApi({
    redirectOnUnauthorized: false,
  })
  const [userLoading, setUserLoading] = useState<boolean>(true)
  const [user, setUser] = useState<User>(initialUserValue)

  const clearUser = () => {
    setUser(initialUserValue)
  }

  const setB2bDetailsForThirdPartyAgent = (user: Partial<User>) => {
    const { businessPartner } = user ?? {}

    if (dispatch) {
      dispatch(setB2BDetails(convertToB2bDetails(businessPartner)))
    }

    addKeywordToStoredSearchQuery(businessPartner?.partnershipCode)
  }

  const getUser = async (): Promise<void> => {
    try {
      if (!userLoading) {
        setUserLoading(true)
      }

      const localAccessToken = getAccessTokenSilently()
      const userObject: CustomAccessToken = jwtDecode(localAccessToken)

      const userId = userObject?.sub

      const userDetailsResponse = await userInfoRequest.get(
        getUserInfoById(userId)
      )

      hasError(userInfoRequest, userDetailsResponse)

      let userType = null

      if (isAuthenticated) {
        if (isThirdPartyAgent) {
          setB2bDetailsForThirdPartyAgent(userDetailsResponse)
        }

        userType = IdentityUserTypes.PATIENT

        if (isSupportUser) {
          userType = IdentityUserTypes.SUPPORT
        } else if (isClinician) {
          userType = IdentityUserTypes.CLINICIAN
        } else if (isThirdPartyAgent) {
          userType = IdentityUserTypes.THIRD_PARTY_SUPPORT
        }

        userDetailsResponse['userType'] = userType
      }

      setUser(userDetailsResponse)

      const requiredFieldsForUserObject = [
        'id',
        'firstName',
        'lastName',
        'email',
        'userType',
      ]
      const tokenExpirationDate = new Date(userObject?.exp * 1000)
      const filteredUserDetails = _pick(
        userDetailsResponse,
        requiredFieldsForUserObject
      )

      Cookies.set(COOKIE_KEYS.USER, filteredUserDetails, {
        expires: tokenExpirationDate,
      })

      Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, localAccessToken, {
        expires: tokenExpirationDate,
        sameSite: process.env.NODE_ENV === 'development' ? 'Lax' : 'None',
        secure: process.env.NODE_ENV !== 'development',
      })

      // Runs after the user is committed above: these are side effects, and a
      // failure in either must not leave the app without a user. Split comes
      // first because user-scoped treatments gate real functionality, whereas
      // logIdentity is analytics-only.
      try {
        initializeSplitUserClient(userDetailsResponse.email)
        logIdentity(userDetailsResponse?.id, userType)
        datadogRum.setUser({ id: userDetailsResponse?.id })
      } catch (sideEffectError) {
        datadogRum.addError(sideEffectError)
      }
    } catch (error) {
      datadogRum.addError(error)
    } finally {
      setUserLoading(false)
    }
  }

  // Must run before the auth/Split init effect below so the synchronous
  // initial onConsentChange lands before initializeSplitAnonymousClient.
  // Subscribes to the Osano primitive directly: this wrapper sits above
  // RudderStackContext.Provider and cannot read analyticsConsentGranted
  // from that context.
  useEffect(() => subscribeToOsanoAnalyticsConsent(setSplitAnalyticsConsent), [
    isOsanoCmpActive,
  ])

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        getUser()
      } else {
        setUserLoading(false)
      }

      initializeSplitAnonymousClient()
    }
  }, [isLoading, isAuthenticated])

  return (
    <UserContext.Provider
      value={{
        businessPartnerId: user?.businessPartnerId,
        isPartnerAssociationActive: user?.isPartnerAssociationActive ?? false,
        businessPartner: user?.businessPartner,
        isActiveB2BUser:
          user.isPartnerAssociationActive && !!user.businessPartnerId,
        isUserLoading: userLoading,
        user,
        getUser,
        clearUser,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserContextWrapper
